/**
 * Screen 6 — one question, four critics, and no agreement.
 *
 * Two tables sit here, chosen by the toggle at the top. **Your cellar** is one
 * typed question put to bottles you own. **A menu** is two photographs — the
 * kitchen's and the list's — put to bottles a restaurant owns, where a food
 * critic reads the food first and briefs all four sommeliers before any of them
 * argues. They share a screen because they are the same act from the reader's
 * side: ask, then read four answers that disagree.
 *
 * The answers are deliberately shown side by side and unranked. Each critic
 * argues from a different brief (`LENSES` and `RESTAURANT_LENSES` in
 * `backend/panel.py`), so picking a winner here would throw away the only thing
 * this screen has that the scan results do not: the disagreement. The reader is
 * the tie-break.
 *
 * Both are one synchronous request rather than a stream. The cellar table is
 * four ungrounded turns and comes back in seconds. A menu is four stages and is
 * nearer half a minute, which is the most this pattern will carry — see
 * `ask_the_menu` in `backend/main.py` for why it still is not worth a progress
 * channel.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import Voice from '../components/Voice'
import { api, uploadSigned } from '../lib/api'
import { downscaleMenu } from '../lib/downscale'
import type { CellarWine, CellarQuestion, MenuQuestion, PairingBrief } from '../lib/types'

const EXAMPLES = [
  "Tonight's dinner is roast lamb — what should I open?",
  'Something for a Tuesday, nothing serious.',
  'My in-laws are here and they only drink white.',
]

// Shown instead of the general ones once a bottle is named, because every one
// of them is a question you can only ask about something in particular.
const SUBJECT_EXAMPLES = [
  'We just finished this — what should we open next?',
  'We had this last week. Something with a similar vibe?',
  'Loved this. What is the opposite, for a change?',
]

/** Mirrors `panel.MAX_SUBJECTS`; the server enforces it, this stops asking. */
const MAX_SUBJECTS = 4

const MENU_EXAMPLES = [
  'Celebrating, but not silly money.',
  'One of us is vegan and I am driving.',
  'I want something I have never heard of.',
]

// The server's own cap, mirrored so a diner finds out at the shutter rather
// than after uploading. See `MAX_MENU_IMAGES` in `backend/menu.py`.
const MAX_PAGES = 8
const MAX_NOTES = 500

type Table = 'cellar' | 'menu'

export default function Ask() {
  const [table, setTable] = useState<Table>('cellar')

  return (
    <div className="space-y-5 pt-5">
      <div>
        <h2 className="font-display text-2xl">Ask the critics</h2>
        <p className="mt-1 text-sm text-stone-400">
          One question, put to every sommelier at once. They each answer for
          something different — the best bottle, the best value, the most
          interesting, the one the food actually wants — so expect an argument.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setTable('cellar')}
          className={`chip ${table === 'cellar' ? 'chip-on' : ''}`}
        >
          Your cellar
        </button>
        <button
          type="button"
          onClick={() => setTable('menu')}
          className={`chip ${table === 'menu' ? 'chip-on' : ''}`}
        >
          A menu
        </button>
      </div>

      {table === 'cellar' ? <AskTheCellar /> : <AskTheMenu />}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Bottles you own
// ---------------------------------------------------------------------------

function AskTheCellar() {
  const client = useQueryClient()
  const [question, setQuestion] = useState('')
  const [subjects, setSubjects] = useState<CellarWine[]>([])
  const [picking, setPicking] = useState(false)

  const history = useQuery({
    queryKey: ['cellarQuestions'],
    queryFn: api.cellarQuestions,
  })
  // The same key the cellar screen uses, so arriving from a tile costs nothing.
  const cellar = useQuery({ queryKey: ['cellar'], queryFn: api.cellar })

  useSubjectFromUrl(cellar.data?.wines, setSubjects)

  const ask = useMutation({
    mutationFn: (text: string) =>
      api.askCellar(
        text,
        subjects.map((wine) => wine.wineId),
      ),
    onSuccess: () => {
      setQuestion('')
      setSubjects([])
      // The answer is only visible through the history list, so it is not on
      // screen until this lands.
      void client.invalidateQueries({ queryKey: ['cellarQuestions'] })
    },
  })

  const asked = history.data?.questions ?? []
  const ready = question.trim().length >= 3

  return (
    <>
      <section className="card space-y-3 p-4">
        <Subjects
          chosen={subjects}
          onChange={setSubjects}
          wines={cellar.data?.wines ?? []}
          open={picking}
          setOpen={setPicking}
          disabled={ask.isPending}
        />

        <textarea
          rows={3}
          value={question}
          onChange={(event) => setQuestion(event.target.value)}
          // `@` is the keystroke people try, so it opens the picker rather
          // than landing in the prose. The character itself is swallowed:
          // what gets sent is the question, and the bottles travel beside it
          // as ids, so a stray '@' in the text would name nothing.
          onKeyDown={(event) => {
            if (event.key === '@' && subjects.length < MAX_SUBJECTS) {
              event.preventDefault()
              setPicking(true)
            }
          }}
          maxLength={500}
          placeholder="What are you eating, who is coming, what sort of evening is it?"
          className="w-full rounded-xl border border-white/10 bg-black/30 p-3 text-sm text-stone-200
            placeholder:text-stone-600 focus:border-wine-500/50 focus:outline-none"
        />

        <div className="flex flex-wrap gap-1.5">
          {(subjects.length ? SUBJECT_EXAMPLES : EXAMPLES).map((example) => (
            <button
              key={example}
              type="button"
              onClick={() => setQuestion(example)}
              className="chip text-left hover:border-white/25"
            >
              {example}
            </button>
          ))}
        </div>

        <button
          type="button"
          className="btn-primary w-full"
          disabled={!ready || ask.isPending}
          onClick={() => ask.mutate(question.trim())}
        >
          {ask.isPending ? 'The critics are arguing…' : 'Ask the critics'}
        </button>

        {ask.isError && (
          <p className="text-xs text-rose-300">{(ask.error as Error).message}</p>
        )}
      </section>

      {ask.isPending && <Waiting />}

      {history.isLoading && <div className="card h-28 animate-pulse opacity-40" />}

      {history.data && !asked.length && !ask.isPending && (
        <p className="pt-6 text-center text-sm text-stone-400">
          Nothing asked yet. Tell them what you are eating.
        </p>
      )}

      <div className="space-y-6">
        {asked.map((entry) => (
          <Answered key={entry.questionId} entry={entry} />
        ))}
      </div>
    </>
  )
}

/** What a bottle is called when nobody has given it a display name. */
function nameOf(wine: CellarWine): string {
  return (
    wine.displayName ||
    [wine.producer, wine.cuvee, wine.vintage].filter(Boolean).join(' ') ||
    'An unlabelled bottle'
  )
}

/**
 * The bottles a question is about, as chips over a picker.
 *
 * A chip rather than a mention living inside the prose. The question is sent
 * to the critics verbatim and the bottles travel beside it as ids, so an
 * inline mention would have to be parsed back out of a string the reader is
 * free to edit — and a half-deleted "@Châtea" would quietly become a question
 * about nothing. A chip cannot be half-deleted. `@` still opens this, because
 * that is the key people reach for.
 *
 * The list offered is the whole cellar and not what is in stock: the most
 * common question here is about a bottle that has just been finished, and
 * filtering those out would hide exactly the one being asked about.
 */
function Subjects({
  chosen,
  onChange,
  wines,
  open,
  setOpen,
  disabled,
}: {
  chosen: CellarWine[]
  onChange: (wines: CellarWine[]) => void
  wines: CellarWine[]
  open: boolean
  setOpen: (open: boolean) => void
  disabled: boolean
}) {
  const [needle, setNeedle] = useState('')
  const full = chosen.length >= MAX_SUBJECTS
  const search = needle.trim().toLowerCase()

  const matches = wines
    .filter((wine) => !chosen.some((pick) => pick.wineId === wine.wineId))
    .filter(
      (wine) =>
        !search ||
        [wine.displayName, wine.producer, wine.cuvee, wine.region, wine.vintage]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(search),
    )

  // The cellar arrives newest sighting first, which is the right default for a
  // question about what was drunk last night. Only the first handful are shown
  // so the picker does not push the question box off a phone screen; the count
  // below says when there are more, rather than truncating silently.
  const shown = matches.slice(0, 6)

  return (
    <div className="space-y-2">
      {chosen.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {chosen.map((wine) => (
            <span key={wine.wineId} className="chip chip-on">
              {nameOf(wine)}
              <button
                type="button"
                aria-label={`Stop asking about ${nameOf(wine)}`}
                disabled={disabled}
                onClick={() =>
                  onChange(chosen.filter((pick) => pick.wineId !== wine.wineId))
                }
                className="-mr-1 text-sm leading-none text-wine-200/70 hover:text-white"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      {!open && !full && (
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen(true)}
          className="chip hover:border-white/25"
        >
          {chosen.length ? '+ another bottle' : '+ About a bottle'}
        </button>
      )}

      {full && (
        <p className="text-xs text-stone-500">
          {MAX_SUBJECTS} bottles is as many as one question can be about.
        </p>
      )}

      {open && (
        <div className="space-y-2 rounded-xl border border-white/10 bg-black/20 p-2.5">
          <input
            autoFocus
            value={needle}
            onChange={(event) => setNeedle(event.target.value)}
            onKeyDown={(event) => event.key === 'Escape' && setOpen(false)}
            placeholder="Which bottle? Producer, region, vintage…"
            className="w-full rounded-full border border-white/10 bg-black/30 px-3 py-2 text-xs
              placeholder:text-stone-600 focus:border-wine-500/60 focus:outline-none"
          />

          {!wines.length && (
            <p className="px-1 py-2 text-xs text-stone-500">
              Nothing in the cellar to point at yet.
            </p>
          )}

          {shown.map((wine) => (
            <button
              key={wine.wineId}
              type="button"
              onClick={() => {
                onChange([...chosen, wine])
                setNeedle('')
                setOpen(false)
              }}
              className="flex w-full items-baseline gap-2 rounded-lg px-2 py-1.5 text-left
                hover:bg-white/5"
            >
              <span className="min-w-0 flex-1 truncate text-xs text-stone-200">
                {nameOf(wine)}
              </span>
              <span className="shrink-0 text-[11px] text-stone-500">
                {wine.bottleCount ? `${wine.bottleCount} left` : 'none left'}
              </span>
            </button>
          ))}

          {wines.length > 0 && !matches.length && (
            <p className="px-1 py-2 text-xs text-stone-500">Nothing matches that.</p>
          )}

          {matches.length > shown.length && (
            <p className="px-1 text-[11px] text-stone-600">
              {matches.length - shown.length} more — keep typing to narrow it.
            </p>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Seed the subject from `/ask?wine=…`, which is how the cellar hands one over.
 *
 * The id is resolved against the cellar rather than trusted, so a link to a
 * wine that is not yours simply does nothing. Resolving also means waiting for
 * the query, hence the ref: the effect runs again when the data lands, and
 * without it a bottle the reader had already removed would come back.
 */
function useSubjectFromUrl(
  wines: CellarWine[] | undefined,
  setSubjects: (wines: CellarWine[]) => void,
): void {
  const [params, setParams] = useSearchParams()
  const requested = params.get('wine')
  const seeded = useRef(false)

  useEffect(() => {
    if (seeded.current || !requested || !wines) return
    seeded.current = true

    const wine = wines.find((candidate) => candidate.wineId === requested)
    if (wine) setSubjects([wine])

    // Dropped from the URL once it has been read, so that a reload — or the
    // back button, three questions later — does not put back a bottle that
    // has since been taken off.
    setParams(
      (current) => {
        current.delete('wine')
        return current
      },
      { replace: true },
    )
  }, [requested, wines, setSubjects, setParams])
}

function Answered({ entry }: { entry: CellarQuestion }) {
  const subjects = entry.subjects ?? []

  return (
    <section className="space-y-3">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="font-display text-lg leading-tight">“{entry.question}”</h3>
        <Asked at={entry.askedAt} />
      </div>

      {subjects.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] text-stone-500">about</span>
          {subjects.map((subject) => (
            <Link
              key={subject.wineId}
              to={`/wine/${subject.wineId}`}
              className="chip hover:border-white/25"
            >
              {subject.displayName ?? 'a bottle'}
            </Link>
          ))}
        </div>
      )}
      <p className="text-[11px] text-stone-600">
        across {entry.winesConsidered}{' '}
        {entry.winesConsidered === 1 ? 'wine' : 'wines'} in stock
      </p>
      <div className="space-y-3">
        {entry.answers.map((answer) => (
          <Voice key={answer.personaId} answer={answer} />
        ))}
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Bottles a restaurant owns
// ---------------------------------------------------------------------------

interface Page {
  id: string
  file: File
  preview: string
}

function AskTheMenu() {
  const client = useQueryClient()
  const [wine, setWine] = useState<Page[]>([])
  const [food, setFood] = useState<Page[]>([])
  const [notes, setNotes] = useState('')
  const [stage, setStage] = useState('')

  const history = useQuery({
    queryKey: ['menuQuestions'],
    queryFn: api.menuQuestions,
  })

  const ask = useMutation({
    mutationFn: async () => {
      // The two stacks are uploaded together and told apart on the way back
      // in, so the ids have to be unique across both — hence the prefix.
      const pages = [
        ...wine.map((page) => ({ page, role: 'w' })),
        ...food.map((page) => ({ page, role: 'f' })),
      ].map((entry, index) => ({ ...entry, imageId: `${entry.role}${index}` }))

      setStage('Shrinking the photographs…')
      const blobs = await Promise.all(
        pages.map(async (entry) => ({
          ...entry,
          blob: await downscaleMenu(entry.page.file),
        })),
      )

      setStage('Uploading…')
      const created = await api.createMenu(
        blobs.map((entry) => ({
          imageId: entry.imageId,
          contentType: 'image/jpeg',
        })),
      )
      const targets = new Map(created.uploads.map((up) => [up.imageId, up.target]))
      await Promise.all(
        blobs.map(async (entry) => {
          const target = targets.get(entry.imageId)
          if (!target) throw new Error(`No upload was minted for ${entry.imageId}.`)
          await uploadSigned(target, entry.blob)
        }),
      )

      setStage('Reading the menus, then arguing…')
      return api.askMenu(created.menuId, {
        wineImageIds: blobs.filter((e) => e.role === 'w').map((e) => e.imageId),
        foodImageIds: blobs.filter((e) => e.role === 'f').map((e) => e.imageId),
        notes: notes.trim(),
      })
    },
    onSuccess: () => {
      setWine([])
      setFood([])
      setNotes('')
      setStage('')
      void client.invalidateQueries({ queryKey: ['menuQuestions'] })
    },
    onError: () => setStage(''),
  })

  const asked = history.data?.questions ?? []
  const total = wine.length + food.length
  const ready = wine.length > 0 && total <= MAX_PAGES

  return (
    <>
      <section className="card space-y-4 p-4">
        <Pages
          label="The wine list"
          hint="Required. Every page — the critics can only pick something that is on it."
          pages={wine}
          onChange={setWine}
          disabled={ask.isPending}
          room={MAX_PAGES - food.length}
        />

        <Pages
          label="The food menu"
          hint="Optional, but it is what makes this worth doing. A food critic reads it and briefs all four sommeliers."
          pages={food}
          onChange={setFood}
          disabled={ask.isPending}
          room={MAX_PAGES - wine.length}
        />

        <div className="space-y-2">
          <h3 className="label">Anything they should know?</h3>
          <textarea
            rows={3}
            value={notes}
            onChange={(event) => setNotes(event.target.value.slice(0, MAX_NOTES))}
            disabled={ask.isPending}
            placeholder="What you fancy, what you cannot eat, what the evening is for."
            className="w-full resize-none rounded-xl border border-white/10 bg-black/30 p-3 text-sm
              text-stone-200 placeholder:text-stone-600 focus:border-wine-500/50 focus:outline-none"
          />
          <div className="flex flex-wrap gap-1.5">
            {MENU_EXAMPLES.map((example) => (
              <button
                key={example}
                type="button"
                onClick={() => setNotes(example)}
                className="chip text-left hover:border-white/25"
              >
                {example}
              </button>
            ))}
          </div>
          <p className="text-xs text-stone-500">
            Allergies and anything else non-negotiable are passed on as hard
            rules, not preferences. {MAX_NOTES - notes.length} characters left.
          </p>
        </div>

        <button
          type="button"
          className="btn-primary w-full"
          disabled={!ready || ask.isPending}
          onClick={() => ask.mutate()}
        >
          {ask.isPending ? stage || 'Working…' : 'Put it to the table'}
        </button>

        {total > MAX_PAGES && (
          <p className="text-xs text-rose-300">
            {MAX_PAGES} pages is the limit for one table; there are {total}.
          </p>
        )}
        {ask.isError && (
          <p className="text-xs text-rose-300">{(ask.error as Error).message}</p>
        )}
      </section>

      {ask.isPending && (
        <>
          <p className="text-center text-xs text-stone-500">
            Two menus, a food critic and four arguments — about half a minute.
          </p>
          <Waiting />
        </>
      )}

      {history.isLoading && <div className="card h-28 animate-pulse opacity-40" />}

      {history.data && !asked.length && !ask.isPending && (
        <p className="pt-6 text-center text-sm text-stone-400">
          No menus yet. Photograph a wine list and see what they make of it.
        </p>
      )}

      <div className="space-y-6">
        {asked.map((entry) => (
          <MenuAnswered key={entry.questionId} entry={entry} />
        ))}
      </div>
    </>
  )
}

function Pages({
  label,
  hint,
  pages,
  onChange,
  disabled,
  room,
}: {
  label: string
  hint: string
  pages: Page[]
  onChange: (pages: Page[]) => void
  disabled: boolean
  room: number
}) {
  const input = useRef<HTMLInputElement>(null)

  const add = (files: FileList | null) => {
    if (!files) return
    const spare = Math.max(0, room - pages.length)
    const added = Array.from(files)
      .slice(0, spare)
      .map((file) => ({
        // `URL.createObjectURL` rather than a data URL: a 3 MB page as base64
        // in React state is a visible jank on a phone.
        id: `${file.name}-${file.lastModified}-${file.size}`,
        file,
        preview: URL.createObjectURL(file),
      }))
    onChange([...pages, ...added])
  }

  const remove = (id: string) => {
    const going = pages.find((page) => page.id === id)
    if (going) URL.revokeObjectURL(going.preview)
    onChange(pages.filter((page) => page.id !== id))
  }

  return (
    <section className="space-y-2">
      <h3 className="label">{label}</h3>
      <div className="grid grid-cols-4 gap-2">
        {pages.map((page) => (
          <figure
            key={page.id}
            className="relative aspect-3/4 overflow-hidden rounded-xl"
          >
            <img src={page.preview} alt="" className="h-full w-full object-cover" />
            <button
              onClick={() => remove(page.id)}
              disabled={disabled}
              aria-label={`Remove this page of ${label.toLowerCase()}`}
              className="absolute top-1 right-1 grid h-6 w-6 place-items-center rounded-full bg-black/70 text-xs"
            >
              ×
            </button>
          </figure>
        ))}

        {pages.length < room && (
          <button
            onClick={() => input.current?.click()}
            disabled={disabled}
            aria-label={`Add a page of ${label.toLowerCase()}`}
            className="grid aspect-3/4 place-items-center rounded-xl border border-dashed
              border-white/15 text-2xl text-stone-500 hover:border-wine-500/60 hover:text-wine-300"
          >
            +
          </button>
        )}
      </div>
      <p className="text-xs text-stone-500">{hint}</p>

      <input
        ref={input}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        className="hidden"
        onChange={(event) => {
          add(event.target.files)
          event.target.value = ''
        }}
      />
    </section>
  )
}

function MenuAnswered({ entry }: { entry: MenuQuestion }) {
  const heading = entry.notes || entry.cuisine || 'The list'

  return (
    <section className="space-y-3">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="font-display text-lg leading-tight">
          {entry.notes ? `“${entry.notes}”` : heading}
        </h3>
        <Asked at={entry.askedAt} />
      </div>

      <p className="text-[11px] text-stone-600">
        {entry.cuisine && `${entry.cuisine} · `}
        {entry.winesConsidered}{' '}
        {entry.winesConsidered === 1 ? 'wine' : 'wines'} on the list
        {entry.winesTrimmed > 0 &&
          ` · ${entry.winesTrimmed} further down it did not fit`}
      </p>

      {entry.transcriptionNote && (
        <p className="text-[11px] text-amber-300/70">{entry.transcriptionNote}</p>
      )}

      {entry.brief && <Brief brief={entry.brief} />}

      <div className="space-y-3">
        {entry.answers.map((answer) => (
          <Voice key={answer.personaId} answer={answer} />
        ))}
      </div>
    </section>
  )
}

/**
 * The food critic, shown once above the four cards rather than as a fifth one.
 *
 * That is the shape of the thing rather than a layout choice: all four
 * sommeliers read this exact brief before answering, so putting it in the same
 * column as their cards would suggest it is a fifth opinion competing with
 * them when it is the premise they all share.
 */
function Brief({ brief }: { brief: PairingBrief }) {
  return (
    <article className="card space-y-2 border-white/10 bg-black/20 p-4">
      <span className="pill border border-white/10 bg-white/5 text-stone-400">
        The food critic
      </span>
      <p className="text-sm leading-snug text-stone-200">{brief.likelyOrder}</p>
      <p className="text-xs leading-relaxed text-stone-400">{brief.wants}</p>
      {brief.hardConstraints.length > 0 && (
        <ul className="space-y-0.5 pt-0.5">
          {brief.hardConstraints.map((rule) => (
            <li key={rule} className="text-[11px] text-amber-200/80">
              — {rule}
            </li>
          ))}
        </ul>
      )}
    </article>
  )
}

// ---------------------------------------------------------------------------
// Shared furniture
// ---------------------------------------------------------------------------

function Waiting() {
  return (
    <div className="space-y-3">
      {[0, 1, 2, 3].map((index) => (
        <div key={index} className="card h-28 animate-pulse opacity-40" />
      ))}
    </div>
  )
}

function Asked({ at }: { at: string }) {
  const when = at ? new Date(at) : null
  if (!when || Number.isNaN(when.getTime())) return null
  return (
    <span className="shrink-0 text-[11px] text-stone-500">
      {when.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })}
    </span>
  )
}
